import React, { useState, useEffect, useCallback } from 'react';
import { 
  format, 
  addDays, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameDay, 
  isToday, 
  isBefore, 
  startOfDay,
  addMonths,
  subMonths
} from 'date-fns';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon, 
  CalendarIcon, 
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon 
} from '@heroicons/react/24/outline';
import availabilityAPI from '../../../../api/availability';
import { formatISTRange } from '../../utils/date';

const AppointmentCalendar = ({ 
  selectedDoctor, 
  onDateSelect, 
  onSlotSelect, 
  selectedDate, 
  selectedSlot 
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availabilityData, setAvailabilityData] = useState({});
  const [loading, setLoading] = useState(false);

  console.log('AppointmentCalendar rendered with selectedDate:', selectedDate);

  const loadMonthAvailability = useCallback(async () => {
    if (!selectedDoctor) return;
    
    try {
      setLoading(true);
      const startDate = startOfMonth(currentMonth);
      const endDate = endOfMonth(currentMonth);
      
      // Get availability for the entire month using proper date formatting
      const response = await availabilityAPI.getDoctorAvailability(selectedDoctor._id || selectedDoctor.id, {
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd')
      });

      
      // Transform the response into a date-indexed object
      const availabilityMap = {};
      const blocks = response?.data?.blocks || [];
      
      
      if (response.success && Array.isArray(blocks)) {
        blocks.forEach(block => {
          
          const date = format(new Date(block.date), 'yyyy-MM-dd');
          if (!availabilityMap[date]) {
            availabilityMap[date] = {
              totalSlots: 0,
              availableSlots: 0,
              slots: []
            };
          }
          
          // Calculate total and available slots for this block
          const blockSlots = block.slots || [];
          
          // Only count slots that are truly available (Open status)
          // Exclude: Expired, Booked, Completed, Held, Closed, Cancelled
          const unavailableStatuses = ['Expired', 'Booked', 'Completed', 'Held', 'Closed', 'Cancelled'];
          const availableSlots = blockSlots.filter(slot => {
            // Check status field if it exists
            if (slot.status) {
              return !unavailableStatuses.includes(slot.status);
            }
            // Fallback to isAvailable check for backward compatibility
            return slot.isAvailable !== false;
          });
          const availableCount = availableSlots.length;
          
          
          availabilityMap[date].totalSlots += blockSlots.length;
          availabilityMap[date].availableSlots += availableCount;
          availabilityMap[date].slots.push(...blockSlots.map(slot => ({
            ...slot,
            formattedTime: formatISTRange(slot.startTime, slot.endTime),
            blockId: block._id
          })));
        });
      }
      
      setAvailabilityData(availabilityMap);
    } catch (error) {
      console.error('Error loading availability:', error);
      // Fallback to mock data for demo
      setAvailabilityData(generateMockAvailability());
    } finally {
      setLoading(false);
    }
  }, [selectedDoctor, currentMonth]);

  // Load availability data for the current month with debouncing
  useEffect(() => {
    if (selectedDoctor) {
      // Add a small delay to prevent rapid-fire API calls
      const timeoutId = setTimeout(() => {
        loadMonthAvailability();
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [selectedDoctor, currentMonth, loadMonthAvailability]);

  const generateMockAvailability = () => {
    const mockData = {};
    const startDate = startOfMonth(currentMonth);
    const endDate = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    
    days.forEach(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      // Only add availability for weekdays and random selection
      if (day.getDay() !== 0 && day.getDay() !== 6 && Math.random() > 0.3) {
        const totalSlots = Math.floor(Math.random() * 8) + 2; // 2-10 slots
        const availableSlots = Math.floor(Math.random() * totalSlots);
        
        mockData[dateStr] = {
          totalSlots,
          availableSlots,
          slots: Array.from({ length: totalSlots }, (_, i) => ({
            id: `slot-${i}`,
            startTime: `${9 + Math.floor(i / 2)}:${i % 2 === 0 ? '00' : '30'}`,
            endTime: `${9 + Math.floor(i / 2)}:${i % 2 === 0 ? '30' : '60'}`,
            isAvailable: i < availableSlots
          }))
        };
      }
    });
    
    return mockData;
  };

  const getCalendarDays = () => {
    const startDate = startOfMonth(currentMonth);
    const endDate = endOfMonth(currentMonth);
    return eachDayOfInterval({ start: startDate, end: endDate });
  };

  const getDayAvailability = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return availabilityData[dateStr] || { totalSlots: 0, availableSlots: 0, slots: [] };
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const handleDateClick = (date) => {
    console.log('Date clicked:', date);
    const availability = getDayAvailability(date);
    console.log('Availability for clicked date:', availability);
    
    if (availability.availableSlots > 0) {
      console.log('Calling onDateSelect with:', date);
      onDateSelect(date);
    } else {
      console.log('No available slots for this date');
    }
  };

  const days = getCalendarDays();
  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Calendar Header */}
      <div className="bg-theme-blue/30 p-4 border-b border-theme-border/20">
        <div className="flex justify-between items-center">
          <button 
            onClick={() => navigateMonth(-1)}
            className="p-2 hover:bg-theme-border/20 rounded-lg transition-colors text-theme-heading"
            disabled={loading}
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          
          <div className="text-center">
            <h2 className="text-lg font-semibold text-theme-heading">
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
          </div>
          
          <button 
            onClick={() => navigateMonth(1)}
            className="p-2 hover:bg-theme-border/20 rounded-lg transition-colors text-theme-heading"
            disabled={loading}
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="p-4">
        {/* Week Days Header */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map(day => (
            <div key={day} className="text-center py-2 text-xs font-medium text-theme-error uppercase">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-2 mb-6">
          {days.map((date) => {
            const availability = getDayAvailability(date);
            const isSelected = selectedDate && isSameDay(date, selectedDate);
            const isPast = isBefore(date, startOfDay(new Date()));
            const hasSlots = availability.availableSlots > 0;
            const todayDate = isToday(date);
            
            // Debug logging for all dates to see comparison
            const dateStr = format(date, 'yyyy-MM-dd');
            const selectedDateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : 'null';
            
            if (dateStr === '2025-09-12' || isSelected) {
              console.log(`Date ${dateStr}: selectedDate=${selectedDateStr}, isSelected=${isSelected}, isSameDay result=${selectedDate ? isSameDay(date, selectedDate) : 'no selectedDate'}`);
            }
            
            return (
              <button
                key={date.toISOString()}
                onClick={() => handleDateClick(date)}
                disabled={isPast || !hasSlots || loading}
                className={`
                  relative w-full aspect-square rounded-lg transition-all duration-200 p-1 text-sm font-medium
                  ${isSelected && hasSlots
                    ? 'bg-theme-border text-white'
                    : hasSlots && !isPast
                    ? 'bg-theme-blue/30 text-theme-border hover:bg-theme-blue/50'
                    : availability.totalSlots > 0 && !hasSlots
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'text-gray-400'
                  }
                  ${isPast ? 'opacity-40' : ''}
                  ${todayDate && !isSelected ? 'ring-1 ring-theme-border' : ''}
                `}
              >
                <div className="flex flex-col items-center justify-center h-full">
                  <span className={`text-sm ${todayDate && !isSelected ? 'text-theme-border font-bold' : ''}`}>
                    {format(date, 'd')}
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="border-t border-gray-100 pt-3 mt-3">
          <div className="flex flex-wrap gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 bg-theme-blue/30 rounded"></div>
              <span className="text-theme-text">Available</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 bg-theme-border rounded"></div>
              <span className="text-theme-text">Selected</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 ring-1 ring-theme-border rounded"></div>
              <span className="text-theme-text">Today</span>
            </div>
          </div>
        </div>

        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center rounded-xl">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-theme-border"></div>
              <span className="ml-3 text-theme-text text-sm">Loading...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentCalendar;
